/**
 * Custom hook for horizontal swipe navigation between tabs.
 * Attaches touch event listeners to a ref element.
 * Distinguishes horizontal swipes from vertical scrolling.
 */

import { useRef, useEffect } from 'react';

interface SwipeConfig {
  /** Minimum horizontal distance (px) to trigger a swipe. Default: 50 */
  minDistance?: number;
  /** Maximum vertical deviation (px) before canceling swipe. Default: 75 */
  maxVerticalDeviation?: number;
  /** Callback when user swipes left (next tab) */
  onSwipeLeft: () => void;
  /** Callback when user swipes right (previous tab) */
  onSwipeRight: () => void;
}

export function useSwipeNavigation(
  ref: React.RefObject<HTMLElement | null>,
  config: SwipeConfig,
) {
  const {
    minDistance = 50,
    maxVerticalDeviation = 75,
    onSwipeLeft,
    onSwipeRight,
  } = config;

  // Store callbacks in refs so touch listeners always see the latest version
  const onSwipeLeftRef = useRef(onSwipeLeft);
  const onSwipeRightRef = useRef(onSwipeRight);
  onSwipeLeftRef.current = onSwipeLeft;
  onSwipeRightRef.current = onSwipeRight;

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    let startX = 0;
    let startY = 0;
    let cancelled = false;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      startX = touch.clientX;
      startY = touch.clientY;
      cancelled = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (cancelled) return;
      const touch = e.touches[0];
      const deltaY = Math.abs(touch.clientY - startY);

      // Cancel swipe detection if user is scrolling vertically
      if (deltaY > maxVerticalDeviation) {
        cancelled = true;
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (cancelled) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - startX;
      const deltaY = Math.abs(touch.clientY - startY);

      // Must be primarily horizontal and exceed minimum distance
      if (Math.abs(deltaX) >= minDistance && Math.abs(deltaX) > deltaY) {
        if (deltaX < 0) {
          onSwipeLeftRef.current();
        } else {
          onSwipeRightRef.current();
        }
      }
    };

    element.addEventListener('touchstart', handleTouchStart, { passive: true });
    element.addEventListener('touchmove', handleTouchMove, { passive: true });
    element.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [ref, minDistance, maxVerticalDeviation]);
}
